"use client";
import { useTranslations } from "next-intl";
import { IBaseLabelValue } from "@/interfaces";
import { useRouter } from "next/navigation";
import { CustomButton, FlexBetween, FlexCenter, FlexCol } from "../../ui";
import ResultContent from "../../resultContent";
import { cn, formatCardNumberGroups } from "@/lib";
import { routes } from "@/routes";
import DownloadPdf from "../../pdfTemplates/downloadPdf";
import DetailCreditCard from "../../detailCreditCard";
import DetailInquiryPdf from "../../pdfTemplates/detailInquiryPdf";
import { useRef } from "react";
import { IRawPurchasedCardToDepositInquiryResData } from "@/interfaces/inquiries/bank";

const CardToAccountInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedCardToDepositInquiryResData;
}) => {
  const t = useTranslations("serviceResponseDetails");
  const router = useRouter();
  const { bank, depositNumber, type } = data.data.data;
  const { number } = data.data.input;

  const pdfContentRef = useRef<HTMLDivElement>(null);
  const resultContent: IBaseLabelValue[] = [
    {
      label: t("cardToAccount.depositNumber"),
      value: depositNumber,
    },
    {
      label: t("cardToAccount.bankName"),
      value: bank,
    },
    {
      label: t("cardToAccount.submittedCardNumber"),
      value: number,
    },
    {
      label: t("cardToAccount.cardType"),
      value: type,
    },
  ];

  return (
    <div className="shadow-main from-emerald-mint relative basis-[500px] rounded-2xl bg-linear-to-b to-transparent p-[2px] lg:basis-[640px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-8">
        <h2 className="mb-8 text-center font-bold lg:text-lg">
          {t("cardToAccount.title")}
        </h2>
        <div className="mx-auto mb-8 sm:w-3/4">
          <DetailCreditCard
            variant="account"
            cardNumber={number}
            cardValues={{
              label: "شماره حساب",
              value: depositNumber,
            }}
            footer={() => (
              <>
                <div className="flex w-full justify-between">
                  <div className="flex flex-col items-center">
                    <span className="text-xs font-light">شماره کارت بانکی</span>
                    <span
                      className="md:text-md text-sm font-semibold"
                      dir="ltr"
                    >
                      {formatCardNumberGroups(number)}
                    </span>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-xs font-light">نوع کارت</span>
                    <span className="md:text-md text-sm font-semibold">
                      {type}
                    </span>
                  </div>
                </div>
              </>
            )}
          />
        </div>
        <FlexBetween className="mb-3 items-center">
          <h2 className="text-center font-bold lg:text-lg">
            {t("cardToAccount.resultTitle")}
          </h2>
          <DownloadPdf
            id="card-to-account-inquiry-download-pdf"
            fileName="کارت-به-حساب"
            tableTitle="کارت به حساب"
            useHtmlToPdf={true}
            htmlContentRef={pdfContentRef}
          />
        </FlexBetween>
        <ResultContent content={resultContent} />
        <FlexCenter className="flex flex-col justify-center md:flex-row md:gap-6">
          <DownloadPdf
            useHtmlToPdf={true}
            htmlContentRef={pdfContentRef}
            id="card-to-account-inquiry-download-pdf"
            fileName="کارت-به-حساب"
            tableTitle="کارت به حساب"
            className="order-2 flex cursor-pointer items-center justify-center rounded-md border-none! bg-red-700 px-8 py-[6px] text-white transition-all hover:bg-red-800 md:order-1 lg:rounded-lg lg:py-[8px]"
            showIcon={false}
            labelClass="text-xs lg:text-sm xl:text-base font-medium"
          />
          <div className="order-1 mb-4 flex gap-4 md:order-2 md:m-0 xl:gap-6">
            <CustomButton
              id="details-card-to-account-inquiry-back"
              className={cn("w-fit bg-gray-500")}
              onClick={() =>
                router.push(routes.main.INQUIRY_HISTORY.primaryPath)
              }
            >
              {t("companyInformationInquiry.backButton")}
            </CustomButton>
            <CustomButton
              id="details-card-to-account-inquiry-new-request"
              onClick={() =>
                router.push(routes.services.Card_To_Account.rewritePath)
              }
              className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
            >
              {t("newRequest")}
            </CustomButton>
          </div>
        </FlexCenter>
      </FlexCol>
      <div
        ref={pdfContentRef}
        style={{
          position: "fixed",
          left: "-9999px",
          top: "-9999px",
          width: "800px",
          fontFamily: "IRANYekan, sans-serif",
          margin: "2rem auto",
        }}
      >
        <DetailInquiryPdf
          subtitle="تبدیل شماره کارت به حساب"
          code={data.code}
          displayComponent={{
            variant: "account",
            cardNumber: number,
            mainValue: {
              label: "شماره حساب",
              value: depositNumber,
            },
            bankName: bank,
            additionalFields: [
              {
                label: "شماره کارت",
                value: formatCardNumberGroups(number),
              },
              {
                label: "نوع کارت",
                value: type,
              },
            ],
          }}
          resultContent={resultContent}
          showResultSection={true}
        />
      </div>
    </div>
  );
};

export default CardToAccountInquiryDetails;
